import React, { useMemo } from "react";
import { Table, Card, Tooltip } from "antd";
import dayjs from "dayjs";

const EcomAggCategoryMappingTable = ({ aggEcomDataCategory }) => {
  // Category mapping from the main component
  const categoryMapping = {
    "BTS": [
      "BTS BOYS K",
      "BTS BOYS T",
      "BTS GIRLS K",
      "BTS GIRLS T",
      "BTS MEN",
      "BTS WOMEN",
      "BTS BOYS K BERHADIAH",
      "BTS BOYS T BERHADIAH"
    ],
    "non BTS": [
      "CASUAL KIDS MALE",
      "CASUAL KIDS FEMALE",
      "CASUAL BOYS",
      "CASUAL GIRLS",
      "PHYLON MEN",
      "PHYLON MEN - MICKELSON",
      "CASUAL MEN",
      "CASUAL WOMEN",
      "OUTDOOR MEN R",
      "BADMINTON MEN R",
      "FUTSAL BOYS R",
      "FUTSAL MEN R",
      "SOCCER MEN R"
    ],
    "Lifestyle": [
      "LIFESTYLE MEN",
      "LIFESTYLE WOMEN"
    ],
    "Basket": [
      "BASKET BOYS R",
      "BASKET MEN R",
      "BASKET WOMEN R"
    ],
    "Running": [
      "RUNNING MEN R",
      "RUNNING WOMEN R"
    ],
    "SDL": [
      "SANDAL MEN",
      "SANDAL WOMEN",
      "SANDAL GIRLS",
      "SPON GUNUNG JEPIT BOYS",
      "SPON JEPIT WOMEN",
      "PHYLON SELOP MEN",
      "SPON GUNUNG JEPIT MEN",
      "SPON GUNUNG SELOP MEN",
      "SPON GUNUNG JEPIT JEMPOL MEN",
      "SANDAL BOYS BERHADIAH"
    ]
  };

  // Build category group table data (similar to buildCategoryTableData)
  const buildCategoryGroupTableData = (aggEcomDataCategory, weekInfo) => {
    if (!aggEcomDataCategory || aggEcomDataCategory.length === 0) return [];

    // Get all categories from data
    const allCategories = [
      ...new Set(aggEcomDataCategory.map((d) => d._id.category)),
    ];
    
    // Get mapped categories
    const mappedCategories = Object.values(categoryMapping).flat();
    
    // Find unmapped categories
    const unmappedCategories = allCategories.filter(cat => !mappedCategories.includes(cat));
    
    // Add "Others" group for unmapped categories
    const extendedCategoryMapping = {
      ...categoryMapping,
      ...(unmappedCategories.length > 0 ? { "Others": unmappedCategories } : {})
    };

    // Get category groups from extended mapping
    const categoryGroups = Object.keys(extendedCategoryMapping);

    // Calculate total per week for % (both QTY and Value)
    const totalPerWeek = {};
    const totalValuePerWeek = {};
    aggEcomDataCategory.forEach((d) => {
      totalPerWeek[d._id.weekNo] = (totalPerWeek[d._id.weekNo] || 0) + d.totalQty;
      totalValuePerWeek[d._id.weekNo] = (totalValuePerWeek[d._id.weekNo] || 0) + (d.totalValue || 0);
    });

    // Build rows for each category group
    const tableRows = categoryGroups.map((group) => {
      const row = { key: group, category: group };
      
      weekInfo.forEach((w) => {
        // Sum up all categories in this group for this week
        let qty = 0;
        let value = 0;
        extendedCategoryMapping[group].forEach((category) => {
          const entry = aggEcomDataCategory.find(
            (d) => d._id.category === category && d._id.weekNo === w.weekNo
          );
          if (entry) {
            qty += entry.totalQty;
            value += (entry.totalValue || 0);
          }
        });
        
        const qtyPercent =
          totalPerWeek[w.weekNo] && qty > 0
            ? ((qty / totalPerWeek[w.weekNo]) * 100).toFixed(1) + "%"
            : "0.0%";
            
        const valuePercent =
          totalValuePerWeek[w.weekNo] && value > 0
            ? ((value / totalValuePerWeek[w.weekNo]) * 100).toFixed(1) + "%"
            : "0.0%";
        
        row[`qty_${w.weekNo}`] = qty;
        row[`pct_${w.weekNo}`] = qtyPercent;
        row[`value_${w.weekNo}`] = value;
        row[`value_pct_${w.weekNo}`] = valuePercent;
      });
      
      return row;
    });

    // Grand total row (all category groups, each week)
    const grandTotal = { key: "Grand Total", category: "Grand Total" };
    weekInfo.forEach((w) => {
      const qtySum = tableRows.reduce(
        (sum, row) => sum + (row[`qty_${w.weekNo}`] || 0),
        0
      );
      const valueSum = tableRows.reduce(
        (sum, row) => sum + (row[`value_${w.weekNo}`] || 0),
        0
      );
      grandTotal[`qty_${w.weekNo}`] = qtySum || "";
      grandTotal[`pct_${w.weekNo}`] = qtySum > 0 ? "100%" : "";
      grandTotal[`value_${w.weekNo}`] = valueSum || "";
      grandTotal[`value_pct_${w.weekNo}`] = valueSum > 0 ? "100%" : "";
    });
    tableRows.push(grandTotal);

    return tableRows;
  };

  // Build category group columns (similar to buildCategoryColumns)
  const buildCategoryGroupColumns = (weekInfo, tableData) => {
    // Find max qty and value per week (not including Grand Total row)
    const maxQtyPerWeek = {};
    const maxValuePerWeek = {};
    weekInfo.forEach((w) => {
      maxQtyPerWeek[w.weekNo] = Math.max(
        ...tableData
          .filter((row) => row.category !== "Grand Total")
          .map((row) => row[`qty_${w.weekNo}`] || 0)
      );
      maxValuePerWeek[w.weekNo] = Math.max(
        ...tableData
          .filter((row) => row.category !== "Grand Total")
          .map((row) => row[`value_${w.weekNo}`] || 0)
      );
    });

    return [
      {
        title: <b>CATEGORY GROUP</b>,
        dataIndex: "category",
        key: "category",
        fixed: "left",
        width: 210,
        render: (v, row) =>
          row.category === "Grand Total" ? (
            <p style={{ color: "#215623", fontWeight: 700 }}>{v}</p>
          ) : (
            <span style={{ fontWeight: 400 }}>{v}</span>
          ),
      },
      ...weekInfo.map((w) => ({
        title: (
          <Tooltip title={`Week ${w.weekNo}`}>
            <span style={{ fontWeight: 600 }}>{w.weekLabel}</span>
          </Tooltip>
        ),
        children: [
          {
            title: "SALES QTY",
            dataIndex: `qty_${w.weekNo}`,
            key: `qty_${w.weekNo}`,
            align: "right",
            render: (v, row) => {
              if (row.category === "Grand Total") {
                return (
                  <b style={{ color: "#215623" }}>
                    {v ? v.toLocaleString("id-ID") : ""}
                  </b>
                );
              }
              // Highlight highest
              return v === maxQtyPerWeek[w.weekNo] && v > 0 ? (
                <b style={{ color: "#1e88e5" }}>{v.toLocaleString("id-ID")}</b>
              ) : v > 0 ? (
                <span style={{ fontWeight: 600 }}>
                  {v.toLocaleString("id-ID")}
                </span>
              ) : (
                <span style={{ color: "#ccc" }}>-</span>
              );
            },
          },
          {
            title: "QTY %",
            dataIndex: `pct_${w.weekNo}`,
            key: `pct_${w.weekNo}`,
            align: "right",
            render: (v, row) =>
              row.category === "Grand Total" ? (
                <b style={{ color: "#215623" }}>{v}</b>
              ) : v === "0.0%" ? (
                <span style={{ color: "#ccc" }}>{v}</span>
              ) : (
                <span>{v}</span>
              ),
          },
          {
            title: "SALES VALUE",
            dataIndex: `value_${w.weekNo}`,
            key: `value_${w.weekNo}`,
            align: "right",
            render: (v, row) => {
              if (row.category === "Grand Total") {
                return (
                  <b style={{ color: "#215623" }}>
                    {v ? `Rp ${v.toLocaleString("id-ID")}` : ""}
                  </b>
                );
              }
              // Highlight highest
              return v === maxValuePerWeek[w.weekNo] && v > 0 ? (
                <b style={{ color: "#1e88e5" }}>Rp {v.toLocaleString("id-ID")}</b>
              ) : v > 0 ? (
                <span style={{ fontWeight: 600 }}>
                  Rp {v.toLocaleString("id-ID")}
                </span>
              ) : (
                <span style={{ color: "#ccc" }}>-</span>
              );
            },
          },
          {
            title: "VALUE %",
            dataIndex: `value_pct_${w.weekNo}`,
            key: `value_pct_${w.weekNo}`,
            align: "right",
            render: (v, row) =>
              row.category === "Grand Total" ? (
                <b style={{ color: "#215623" }}>{v}</b>
              ) : v === "0.0%" ? (
                <span style={{ color: "#ccc" }}>{v}</span>
              ) : (
                <span>{v}</span>
              ),
          },
        ],
      })),
    ];
  };

  // Week headers (same as Category Sales table)
  const weekInfo = useMemo(() => {
    if (!aggEcomDataCategory || aggEcomDataCategory.length === 0) return [];
    const result = [];
    aggEcomDataCategory.forEach((d) => {
      if (!result.find((w) => w.weekNo === d._id.weekNo)) {
        const start = dayjs(d.weekStartDate).format("D MMM");
        const end = dayjs(d.weekEndDate).format("D MMM");
        result.push({
          weekNo: d._id.weekNo,
          weekLabel: `${start} - ${end}`,
        });
      }
    });
    result.sort((a, b) => a.weekNo - b.weekNo);
    return result;
  }, [aggEcomDataCategory]);

  // Table data
  const tableData = useMemo(
    () => buildCategoryGroupTableData(aggEcomDataCategory, weekInfo),
    [aggEcomDataCategory, weekInfo]
  );

  // Columns
  const columns = useMemo(
    () => buildCategoryGroupColumns(weekInfo, tableData),
    [weekInfo, tableData]
  );

  if (!aggEcomDataCategory || aggEcomDataCategory.length === 0)
    return <div>No data</div>;

  return (
    <Card
      title={<span style={{ fontWeight: 600 }}>Category Group Performance per Week</span>}
      style={{
        width: "100%",
        margin: "30px 0",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        borderRadius: 18,
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        size="middle"
        scroll={{ x: "max-content" }}
        style={{
          borderRadius: 16,
          background: "#fff",
        }}
        rowClassName={(row) =>
          row.category === "Grand Total"
            ? "ant-table-row-grandtotal"
            : undefined
        }
      />
    </Card>
  );
};

export default EcomAggCategoryMappingTable;
